import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import {LoginRequest, LoginResponse, ApiResponse, UserLogged} from '../types';
import api from "../services/api";

interface AuthContextType {
    isAuthenticated: boolean;
    user: UserLogged | null;
    token: string | null;
    login: (credentials: LoginRequest) => Promise<boolean>;
    logout: () => void;
    isLoading: boolean;
    fetchUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [user, setUser] = useState<UserLogged | null>(null);
    const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
    const [isLoading, setIsLoading] = useState(true);

    const fetchUser = useCallback(async () => {
        const currentToken = localStorage.getItem('token');
        if (currentToken) {
            try {
                api.defaults.headers.common['Authorization'] = `Bearer ${currentToken}`;
                const response = await api.get<ApiResponse<LoginResponse>>('/api/users/check');

                if (response.data.status) {
                    const loginData = response.data.data;
                    const { email, name, surname, role, client } = loginData;
                    setUser({ email, name, surname, role, client });
                } else {
                    throw new Error("Token validation failed");
                }

            } catch (error) {
                console.error("Session check failed:", error);
                setToken(null);
                setUser(null);
                localStorage.removeItem('token');
                delete api.defaults.headers.common['Authorization'];
            }
        }
    }, []);

    useEffect(() => {
        const validateInitialToken = async () => {
            setIsLoading(true);
            await fetchUser();
            setIsLoading(false);
        };
        validateInitialToken();
    }, [fetchUser]);

    const login = async (credentials: LoginRequest) => {
        const response = await api.post<ApiResponse<LoginResponse>>('/api/auth/public/login', credentials);

        const loginData = response.data.data;
        const { token: newToken, id, email, name, surname, role, client } = loginData;
        if(id === -1) {
            window.location.href = "/notConfirmed/" + newToken;
        } else if(id === -2) {
            window.location.href = "/notApproved";
        }else {
            setToken(newToken);
            setUser({ email, name, surname, role, client });
            localStorage.setItem('token', newToken);
            api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
            return client;
        }
        return false;
    };

    const logout = () => {
        setUser(null);
        setToken(null);
        localStorage.removeItem('token');
        delete api.defaults.headers.common['Authorization'];
    };

    const value = {
        isAuthenticated: !!token,
        user,
        token,
        login,
        logout,
        isLoading,
        fetchUser,
    };

    return (
        <AuthContext.Provider value={value}>
            {!isLoading && children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth deve essere usato all\'interno di un AuthProvider');
    }
    return context;
};
